import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exportAuditLogsCsv, fetchAuditLogDetail, fetchAuditLogs } from '../utils/adminAuditApi';
import Layout from '../components/Layout';
import Input from '../components/elements/Input';
import Button from '../components/elements/Button';
import Loading from '../components/elements/Loading';
import Notification from '../components/Notification';
import { Download, Eye, RefreshCcw, Search, ShieldCheck } from 'lucide-react';
import JsonDetailsModal from '../components/JsonDetailsModal';

const DEFAULT_LIMIT = 50;

const EMPTY_FILTERS = {
  search: '',
  event: '',
  severity: '',
  actorRole: '',
  method: '',
  requestId: '',
  actorUserId: '',
  fromLocal: '',
  toLocal: '',
};

const toPositiveInt = (value, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.floor(n);
};

const toIso = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
};

const formatIsoDateTime = (isoValue) => {
  if (!isoValue) return '-';
  const parsed = new Date(isoValue);
  if (Number.isNaN(parsed.getTime())) return isoValue;
  return parsed.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const severityClass = {
  info: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  warn: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30',
  error: 'bg-red-500/20 text-red-400 border border-red-500/30',
  critical: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
};

export default function AdminAuditLogsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';

  const [loading, setLoading] = useState(true);
  const [pageReady, setPageReady] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: 'success' });

  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_LIMIT, total: 0, totalPages: 1, hasNext: false, hasPrev: false });
  const [filterOptions, setFilterOptions] = useState({ severities: [], actorRoles: [], methods: [] });
  const [jsonText, setJsonText] = useState('');

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const readStateFromUrl = () => {
    const nextFilters = {
      search: searchParams.get('search') || '',
      event: searchParams.get('event') || '',
      severity: searchParams.get('severity') || '',
      actorRole: searchParams.get('actorRole') || '',
      method: searchParams.get('method') || '',
      requestId: searchParams.get('requestId') || '',
      actorUserId: searchParams.get('actorUserId') || '',
      fromLocal: searchParams.get('fromLocal') || '',
      toLocal: searchParams.get('toLocal') || '',
    };

    const nextPage = toPositiveInt(searchParams.get('page'), 1);
    const nextLimit = toPositiveInt(searchParams.get('limit'), DEFAULT_LIMIT);

    return { nextFilters, nextPage, nextLimit };
  };

  const writeStateToUrl = (nextFilters, nextPage = 1, nextLimit = DEFAULT_LIMIT) => {
    const nextParams = new URLSearchParams();

    if (nextPage > 1) nextParams.set('page', String(nextPage));
    if (nextLimit !== DEFAULT_LIMIT) nextParams.set('limit', String(nextLimit));

    Object.entries(nextFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        nextParams.set(key, String(value));
      }
    });

    setSearchParams(nextParams);
  };

  useEffect(() => {
    document.title = 'Audit Logs | Eternal Locker™';
    if (!isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const { nextFilters, nextPage, nextLimit } = readStateFromUrl();
    setFilters(nextFilters);
    setPagination((prev) => ({ ...prev, page: nextPage, limit: nextLimit }));
    loadAuditLogs(nextPage, nextFilters, nextLimit);
  }, [searchParams, isLoggedIn]);

  const loadAuditLogs = async (requestedPage = pagination.page, activeFilters = filters, requestedLimit = pagination.limit) => {
    setLoading(true);
    const data = await fetchAuditLogs({
      page: requestedPage,
      limit: requestedLimit,
      search: activeFilters.search.trim(),
      event: activeFilters.event.trim(),
      severity: activeFilters.severity,
      actorRole: activeFilters.actorRole,
      method: activeFilters.method,
      requestId: activeFilters.requestId.trim(),
      actorUserId: activeFilters.actorUserId,
      from: toIso(activeFilters.fromLocal),
      to: toIso(activeFilters.toLocal),
    });
    if (data.success) {
      setLogs(data.logs || []);
      setPagination(data.pagination || { page: 1, limit: DEFAULT_LIMIT, total: 0, totalPages: 1, hasNext: false, hasPrev: false });
      setFilterOptions(data.filterOptions || { severities: [], actorRoles: [], methods: [] });
      setPageReady(true);
    } else {
      setNotification({ message: data.message || 'Failed to load audit logs.', type: 'error' });
    }
    setLoading(false);
  };

  const applyFilters = () => {
    writeStateToUrl(filters, 1, pagination.limit);
  };

  const clearFilters = () => {
    const resetFilters = { ...EMPTY_FILTERS };
    setFilters(resetFilters);
    writeStateToUrl(resetFilters, 1, pagination.limit);
  };

  const changePage = (nextPage) => {
    if (nextPage < 1 || nextPage > (pagination.totalPages || 1)) return;
    writeStateToUrl(filters, nextPage, pagination.limit);
  };

  const handleExport = async () => {
    try {
      const blob = await exportAuditLogsCsv({
        ...filters,
        from: toIso(filters.fromLocal),
        to: toIso(filters.toLocal),
        limit: 5000,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setNotification({ message: 'Audit logs exported successfully.', type: 'success' });
    } catch (_err) {
      setNotification({ message: 'Failed to export audit logs.', type: 'error' });
    }
  };

  const handleViewDetails = async (row) => {
    const data = await fetchAuditLogDetail(row.id);
    if (!data.success) {
      setNotification({ message: data.message || 'Failed to fetch audit log details.', type: 'error' });
      return;
    }

    let parsedPayload = data.log.payload;
    if (typeof parsedPayload === 'string') {
      try {
        parsedPayload = JSON.parse(parsedPayload);
      } catch {
        parsedPayload = data.log.payload;
      }
    }

    const detailView = {
      ...data.log,
      payload: parsedPayload,
    };
    setJsonText(JSON.stringify(detailView, null, 2));
  };

  if (loading && !pageReady) {
    return (
      <Layout full={true}>
        <Loading fullScreen={false} />
      </Layout>
    );
  }

  return (
    <Layout full={true}>
      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: '', type: 'success' })}
      />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ShieldCheck size={28} /> Audit Logs
          </h1>
          <p className="text-sm opacity-70 mt-1">Track administrative and security-relevant events</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleExport} variant="secondary">
            <Download size={18} /> Export CSV
          </Button>
          <Button onClick={() => loadAuditLogs(pagination.page, filters, pagination.limit)} variant="secondary">
            <RefreshCcw size={18} /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-primary-light border border-accent-main rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input
            type="text"
            placeholder="Search event, path, request id..."
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          />
          <Input
            type="text"
            placeholder="Event (exact)"
            value={filters.event}
            onChange={(e) => setFilters((prev) => ({ ...prev, event: e.target.value }))}
          />
          <Input
            type="select"
            value={filters.severity}
            onChange={(e) => setFilters((prev) => ({ ...prev, severity: e.target.value }))}
          >
            <option value="">All Severities</option>
            {filterOptions.severities.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </Input>
          <Input
            type="select"
            value={filters.actorRole}
            onChange={(e) => setFilters((prev) => ({ ...prev, actorRole: e.target.value }))}
          >
            <option value="">All Actor Roles</option>
            {filterOptions.actorRoles.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </Input>

          <Input
            type="select"
            value={filters.method}
            onChange={(e) => setFilters((prev) => ({ ...prev, method: e.target.value }))}
          >
            <option value="">All Methods</option>
            {filterOptions.methods.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </Input>
          <Input
            type="text"
            placeholder="Request ID"
            value={filters.requestId}
            onChange={(e) => setFilters((prev) => ({ ...prev, requestId: e.target.value }))}
          />
          <Input
            type="number"
            placeholder="Actor User ID"
            value={filters.actorUserId}
            onChange={(e) => setFilters((prev) => ({ ...prev, actorUserId: e.target.value }))}
          />
          <Input
            type="datetime-local"
            value={filters.fromLocal}
            onChange={(e) => setFilters((prev) => ({ ...prev, fromLocal: e.target.value }))}
          />
          <Input
            type="datetime-local"
            value={filters.toLocal}
            onChange={(e) => setFilters((prev) => ({ ...prev, toLocal: e.target.value }))}
          />
        </div>

        <div className="flex items-center gap-2 mt-4">
          <Button onClick={applyFilters}>
            <Search size={18} /> Apply Filters
          </Button>
          <Button onClick={clearFilters} variant="secondary">Clear</Button>
          <span className="text-xs opacity-70 ml-auto">Showing {logs.length} of {pagination.total} logs</span>
        </div>
      </div>

      <div className="bg-primary-light rounded-lg border border-accent-main overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-accent-main border-b border-accent-main">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Time</th>
              <th className="px-4 py-3 text-left font-semibold">Event</th>
              <th className="px-4 py-3 text-left font-semibold">Severity</th>
              <th className="px-4 py-3 text-left font-semibold">Actor</th>
              <th className="px-4 py-3 text-left font-semibold">Request</th>
              <th className="px-4 py-3 text-left font-semibold">Path</th>
              <th className="px-4 py-3 text-left font-semibold">Hash</th>
              <th className="px-4 py-3 text-center font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {logs.length ? logs.map((row) => (
              <tr key={row.id} className="border-b border-accent-main opacity-80 hover:opacity-100">
                <td className="px-4 py-3 whitespace-nowrap">{formatIsoDateTime(row.timestampIso)}</td>
                <td className="px-4 py-3 font-semibold">{row.event}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${severityClass[row.severity] || 'bg-gray-100 text-gray-800'}`}>
                    {row.severity}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs">
                    <div>{row.actorRole || '-'}</div>
                    <div className="opacity-70">UID: {row.actorUserId || '-'}</div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs">
                    <div className="font-semibold">{row.method || '-'}</div>
                    <div className="opacity-70 truncate max-w-[180px]" title={row.requestId || ''}>{row.requestId || '-'}</div>
                  </div>
                </td>
                <td className="px-4 py-3 max-w-[260px] truncate" title={row.path || ''}>{row.path || '-'}</td>
                <td className="px-4 py-3 text-xs font-mono max-w-[200px] truncate" title={row.payloadHash || ''}>{row.payloadHash || '-'}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleViewDetails(row)}
                    className="text-accent-main hover:opacity-75"
                    title="View details"
                  >
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center opacity-60">No audit logs found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-end gap-2 mt-4">
        <Button onClick={() => changePage(pagination.page - 1)} variant="secondary" disabled={!pagination.hasPrev}>Previous</Button>
        <span className="text-sm opacity-70">Page {pagination.page} of {pagination.totalPages}</span>
        <Button onClick={() => changePage(pagination.page + 1)} variant="secondary" disabled={!pagination.hasNext}>Next</Button>
      </div>

      <JsonDetailsModal open={Boolean(jsonText)} jsonText={jsonText} onClose={() => setJsonText('')} />
    </Layout>
  );
}
